import React, { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled';
import { useHubContext } from '@atlassian/company-hub-common';
import { HubTypographyScope } from '@atlassian/company-hub-common/hub-typography-scope';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { SearchTextField } from '../article-search-component/search-textfield';
import { HelpObjectSearchDropdown } from './search-dropdown';

const styles = cssMap({
	responsiveTrigger: {
		width: '400px',
		maxWidth: '100%',
	},
});

export type HelpObjectSearchComponentProps = { isEditor: boolean; placeholderText?: string };

export const HelpObjectSearchComponent = ({
	isEditor,
	placeholderText,
}: HelpObjectSearchComponentProps): React.JSX.Element => {
	const [queryWord, setQueryWord] = useState<string>('');
	const [showDropdown, setShowDropdown] = useState(false);
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [_, setInputWidth] = useState(inputBoxRef.current?.offsetWidth);

	const {
		cloudId,
		env: { helpCenterAri, relativeBaseUrl },
	} = useHubContext();

	if (!helpCenterAri || !cloudId) {
		throw new Error('Help center Ari and cloud ID are required');
	}

	useEffect(() => {
		const listener = () => setInputWidth(inputBoxRef.current?.offsetWidth);
		const unbind = bind(window, {
			type: 'resize',
			listener,
		});
		return unbind;
	}, []);

	const closeDropdown = () => {
		if (showDropdown) {
			setShowDropdown(false);
		}
	};

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const handleInput = (searchInput: string) => {
				setQueryWord(searchInput);
				setShowDropdown(searchInput.trim().length > 0);
			};

			return (
				<Box
					ref={inputBoxRef}
					xcss={cx(fg('hubs_jsm_max_width_appearance') && styles.responsiveTrigger)}
				>
					<SearchTextField
						keyboardRef={inputRef}
						{...triggerProps}
						ref={triggerProps.ref}
						onInput={handleInput}
						isReadOnly={isEditor}
						placeholderText={placeholderText}
						showDropdown={showDropdown}
						queryWord={queryWord}
						searchBasePath={relativeBaseUrl || undefined}
					/>
				</Box>
			);
		},
		[isEditor, placeholderText, queryWord, relativeBaseUrl, showDropdown],
	);

	return (
		<Popup
			shouldReturnFocus={false}
			content={(contentProps) => {
				return (
					// Not redundant with the scope HubAppShell mounts: `Popup` portals to
					// `body`, so the dropdown escapes that subtree and loses the hub fonts.
					<HubTypographyScope>
						<KeyboardHighlightProvider listenerNode={inputRef?.current}>
							<HelpObjectSearchDropdown
								{...contentProps}
								cloudId={cloudId}
								helpCenterAri={helpCenterAri || ''}
								inputWidth={inputBoxRef.current?.offsetWidth || 0}
								queryWord={queryWord}
							/>
						</KeyboardHighlightProvider>
					</HubTypographyScope>
				);
			}}
			isOpen={showDropdown}
			autoFocus={false}
			placement="bottom-start"
			trigger={trigger}
			onClose={closeDropdown}
		/>
	);
};
